import { Logger } from "@viamap/viamap2-common";
import { GenerateGeom } from "./GenerateGeom";
import { PropertyInfoInterface } from "./PropertyInfoInterface";
import { feature, featureCollection } from "@turf/helpers";
import { DC } from "src/dataCollector/DataCollector";
import { parse as WKTparse } from "wkt"
import { GenerateGeomUtils } from "./GenerateGeomUtils";

export type CadasterGeoCodeResult = any;

export class CadasterInterface {

    static async searchMatrikel(ejerlavkode: string, matrikelnr: string) {
        let response = (await DC.DF_GQL.Mat2.JordstykkeOpslag(matrikelnr, ejerlavkode))
        let mat = response.MAT_Jordstykke.nodes?.at(0);
        let tmp = WKTparse(mat?.centroideLiggerIndenForJordstykke?.nodes?.[0]?.geometri?.wkt ?? "")
        let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
        return {
            sfeNr: mat?.samletFastEjendomLokalId ?? "",
            matnr: matrikelnr,
            ejerlavkode: ejerlavkode,
            ejerlavsnavn: mat?.jordstykkeLiggerIEjerlav?.ejerlavsnavn ?? "",
            vejareal: mat?.vejareal ?? 0,
            registreretAreal: mat?.registreretAreal ?? 0,
            kommunenavn: mat?.jordstykkeLiggerIMatrikelKommune?.kommunenavn ?? "",
            kommunekode: mat?.jordstykkeLiggerIMatrikelKommune?.kommunekode ?? "",
            lat: coords?.lat,
            lng: coords?.lng,
            utm32N: mat?.centroideLiggerIndenForJordstykke?.nodes?.[0]?.geometri?.wkt ?? "",
        }
    }

    static async geocode(
        elavskode: string[],
        matrikelnr: string[],
        withPolygon: boolean,
        polygonForFullSFE: boolean,
        callback: (result: CadasterGeoCodeResult[]) => void,
        statusStarted: (noGeoCodeStarted: number) => void,
        statusCompleted: (noGeoCodeCompleted: number, stats: any) => void) {
        let completed = 0;
        let stats: Record<string, number> = {};
        let results: CadasterGeoCodeResult[] = [];


        function startIterationWLimitedConcurrency(i: number, activeRequestsLimit: number) {
            let elav = elavskode[i];
            let matrnr = matrikelnr[i];

            let activeRequests = (i + 1) - completed;
            if (activeRequests > activeRequestsLimit) {
                // Delay starting new requests before the queue is shorter. Test again in 1 sek.
                let delay = 1000 * 1;
                setTimeout(
                    () => {
                        startIterationWLimitedConcurrency(i, activeRequestsLimit);
                    },
                    delay);
            } else {
                statusStarted(i + 1);
                CadasterInterface.washFunctionGsearch(elav, matrnr, withPolygon, polygonForFullSFE, i).then((response) => {
                    // CadasterInterface.washFunction(elav, matrnr, withPolygon, polygonForFullSFE, i).then((response) => {

                    completed += 1;
                    const q = response.dataquality;
                    stats = { ...stats, [q]: stats[q] + 1 };
                    statusCompleted(completed, stats);

                    let index = response.idx;
                    // Store result in the right index
                    results[index] = response;

                    // Done?
                    if (completed === elavskode.length) {
                        Logger.logInfo("CadasterInterface", "geocode", "Completed. Stats: " + JSON.stringify(stats));
                        callback(results);
                    }
                });
                // Start next request, if any
                if (i + 1 < elavskode.length) {
                    startIterationWLimitedConcurrency(i + 1, activeRequestsLimit);
                }
            }
        }
        startIterationWLimitedConcurrency(0, 50);
    }

    private static async washFunctionGsearch(elav: string | number, matr: string, withPolygon: boolean, polygonForFullSFE: boolean, idx: number): Promise<CadasterGeoCodeResult> {
        if (matr == "") {
            return {
                "input": `${elav} - ${matr}`,
                "idx": idx,
                "dataquality": "error",
                "dataqualityNote": "No Matrikelnummer",
            }
        }
        if (typeof elav === "string" && !(parseInt(elav).toString() == elav.trim())) {
            return {
                "input": `${elav} - ${matr}`,
                "idx": idx,
                "dataquality": "error",
                "dataqualityNote": "Invalid Ejerlavskode",
            }
        }

        type gSearchReturnMatrikel = {
            ejerlavskode: number;
            kommunenavn: string;
            ejerlavsnavn: string;
            jordstykke_id: number;
            centroid_x: number;
            matrikelnummer: string;
            centroid_y: number;
            geometri: any;
            kommunekode: string;
            visningstekst: string;
            bfenummer: number;
        }

        const search: void | gSearchReturnMatrikel[] = await PropertyInfoInterface.gSearch("matrikel", String(matr), 10, `ejerlavskode='${elav}'`).catch((err) => {
            console.error("(WashFunctionGsearch: gSearch): " + err);
        });
        if (!search || !search.length) {
            return {
                "input": `${elav} - ${matr}`,
                "idx": idx,
                "dataquality": "error",
                "dataqualityNote": "No search result",
            };
        }
        let searchFilter = search.filter((j) => j.matrikelnummer == String(matr));
        if (searchFilter.length == 0) {
            return {
                "input": `${elav} - ${matr}`,
                "idx": idx,
                "dataquality": "error",
                "dataqualityNote": "No match in search result",
            }
        }
        if (searchFilter.length > 1) {
            return {
                "input": `${elav} - ${matr}`,
                "idx": idx,
                "dataquality": "error",
                "dataqualityNote": "Multiple matches in search result",
            }
        }
        let jord = searchFilter[0];
        let geojson = {}
        if (withPolygon) {
            if (polygonForFullSFE) {
                geojson = await PropertyInfoInterface.getGeojsonOfSFE(jord.bfenummer).catch((err) => {
                    console.error("(WashFunctionGsearch: getSFEFromMatrikel): " + err);
                    return { features: [] };
                });
            } else {
                geojson = featureCollection([feature(jord.geometri, {
                    "Matrikelnummer": jord.matrikelnummer,
                    "Ejerlavskode": jord.ejerlavskode,
                    "Ejerlavsnavn": jord.ejerlavsnavn,
                })]);
            }
        }
        let { x: easting, y: northing } = GenerateGeom.convertFromLatLngToUTM({ lat: jord.centroid_y, lng: jord.centroid_x });

        return {
            "input": `${elav} - ${matr}`,
            "idx": idx,
            "dataquality": "good",
            "sfeejendomsnr": jord.bfenummer,
            "lat": jord.centroid_y,
            "lon": jord.centroid_x,
            "easting": easting,
            "northing": northing,
            "dataqualityNote": "Ok",
            ...geojson && { geoJson: geojson }
        }
    }
}